/**
 * TimeSavedBreakdownModal — click-to-modal drill-down for the Time-saved
 * timeline chart on the Overview tab.
 *
 * v1.4 UX fix (2026-04-24): all table rendering is delegated to the shared
 * `TraceTable` component so this modal and the Rating / Latency / Complexity
 * drill-downs render identical markup at every viewport. User ask:
 *   "нужен унифицированный компонент, а не хуева тьма разрозненных".
 *
 * Data source: `useTimeSavedBreakdownItems(filters, bucket, granularity)` —
 * enabled only when `bucket !== null`.
 */

import * as React from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  TraceTable,
  sortRows,
  type TraceColumnKey,
  type TraceTableRow,
  type TraceTableSort,
  type TraceSortKey,
} from '@/components/TraceTable';
import { useTimeSavedBreakdownItems } from '@/hooks/use-time-saved-series';
import type { Filters } from '@/lib/filters-schema';
import { formatBucketLabel } from '@/lib/format-bucket';
import type { TimeSavedBreakdownItem } from '@/services/api-types';

interface TimeSavedBreakdownModalProps {
  /** ISO timestamp of the clicked bucket — `null` when closed. */
  bucket: string | null;
  /** Resolved granularity from the chart — must match so the window agrees. */
  granularity: Filters['granularity'];
  filters: Filters;
  onClose: () => void;
}

const COLUMNS: TraceColumnKey[] = [
  'time',
  'user',
  'role',
  'kind',
  'complexity',
  'rating',
  'minSaved',
  'latency',
  'cost',
  'queryAnswer',
  'feedback',
];

const DEFAULT_SORT: TraceTableSort = { key: 'timeSavedMinutes', dir: 'desc' };

/**
 * Summary-line formatter: minutes → hours with one decimal when < 10h,
 * otherwise integer. We display hours (not minutes) in the modal footer
 * because "total 47.3 h saved" reads as a real business outcome — "2838 min
 * saved" reads as a machine number. Individual row `minSaved` stays in
 * minutes (TraceTable column owns that formatting and the column is labelled
 * "Min saved", so unit agreement holds).
 */
function formatHours(minutes: number): string {
  if (minutes === 0) return '0';
  const h = minutes / 60;
  if (h < 0.1) return h.toFixed(2);
  if (h < 10) return h.toFixed(1);
  return Math.round(h).toString();
}

/**
 * Map the hook's `TimeSavedBreakdownItem` shape onto the TraceTable superset
 * (which expects `userId` + `cost` — we alias from `username` + `totalCost`).
 */
function toTableRow(item: TimeSavedBreakdownItem): TraceTableRow {
  return {
    traceId: item.traceId,
    timestamp: item.timestamp,
    userId: item.username,
    realName: item.realName,
    name: item.name,
    complexity: item.complexity,
    rating: item.rating,
    timeSavedMinutes: item.timeSavedMinutes,
    latencyMs: item.latencyMs,
    cost: item.totalCost,
    input: item.input,
    output: item.output,
    error: item.error,
    textFeedback: item.textFeedback,
    isRm: item.isRm,
  };
}

export function TimeSavedBreakdownModal({
  bucket,
  granularity,
  filters,
  onClose,
}: TimeSavedBreakdownModalProps): React.ReactElement {
  const q = useTimeSavedBreakdownItems(filters, bucket, granularity);
  const [sort, setSort] = React.useState<TraceTableSort>(DEFAULT_SORT);
  // Per-row expand state — same pattern as TraceDrillDownModal. Lets the user
  // open the full request/response (and, for briefs, the delivered Slack
  // content via TraceDetailPanel) inline. Reset on each new bucket so reopening
  // never carries forward expanded rows from the previous slice.
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(
    () => new Set(),
  );

  // Reset sort + expansion each time a new bucket opens so repeat clicks start fresh.
  React.useEffect(() => {
    if (bucket !== null) {
      setSort(DEFAULT_SORT);
      setExpandedIds(new Set());
    }
  }, [bucket]);

  const handleToggleExpanded = React.useCallback((traceId: string) => {
    setExpandedIds((prev) => {
      // Immutable update — TraceTable branches on Set identity, not contents.
      const next = new Set(prev);
      if (next.has(traceId)) next.delete(traceId);
      else next.add(traceId);
      return next;
    });
  }, []);

  const open = bucket !== null;
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) onClose();
    },
    [onClose],
  );

  const onSort = React.useCallback((key: TraceSortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : {
            key,
            dir:
              key === 'timestamp' || key === 'name' || key === 'username'
                ? 'asc'
                : 'desc',
          },
    );
  }, []);

  const titleLabel =
    bucket === null
      ? ''
      : formatBucketLabel(
          bucket,
          granularity === 'auto' ? 'auto' : granularity,
        );

  const items = q.data?.items ?? [];
  const rows = React.useMemo(() => items.map(toTableRow), [items]);
  const sorted = React.useMemo(() => sortRows(rows, sort), [rows, sort]);

  const totalMinutes = items.reduce(
    (acc, i) => acc + (i.timeSavedMinutes || 0),
    0,
  );
  // RM / Non-RM split — both counts and minutes saved, since on this modal
  // the minutes are the headline metric. `isRm` comes from the per-item
  // payload (backed by the `user_role_cache` scalar subquery in the route).
  const rmItems = items.filter((i) => i.isRm);
  const nonRmItems = items.filter((i) => !i.isRm);
  const rmMinutes = rmItems.reduce(
    (acc, i) => acc + (i.timeSavedMinutes || 0),
    0,
  );
  const nonRmMinutes = nonRmItems.reduce(
    (acc, i) => acc + (i.timeSavedMinutes || 0),
    0,
  );
  // Contacts batches — inside the RM / Non-RM split above, named on their own because their
  // minutes are per contact, not per answer (one row per batch CRM answered).
  const contactsItems = items.filter((i) => i.isContacts === true);
  const contactsMinutes = contactsItems.reduce(
    (acc, i) => acc + (i.timeSavedMinutes || 0),
    0,
  );

  const from = bucket ?? '';
  const to = q.data?.bucketEnd ?? '';

  const footer =
    q.data && sorted.length > 0 ? (
      <>
        <span className="font-semibold text-foreground">{sorted.length}</span>{' '}
        request{sorted.length === 1 ? '' : 's'}
        {q.data.truncated && (
          <span className="ml-1 text-amber-600 dark:text-amber-400">
            (capped at {q.data.limit} — narrow the timeframe for more)
          </span>
        )}
        {' · total '}
        <span className="font-semibold text-foreground">
          {formatHours(totalMinutes)}
        </span>{' '}
        h saved
        <span className="mx-1">·</span>
        RM{' '}
        <span className="font-semibold text-foreground">{rmItems.length}</span>{' '}
        ({formatHours(rmMinutes)}h)
        {' · '}
        Non-RM{' '}
        <span className="font-semibold text-foreground">{nonRmItems.length}</span>{' '}
        ({formatHours(nonRmMinutes)}h)
        {contactsItems.length > 0 && (
          <span data-testid="time-saved-breakdown-contacts">
            {' · of which Contacts '}
            <span className="font-semibold text-foreground">
              {contactsItems.length}
            </span>{' '}
            batch{contactsItems.length === 1 ? '' : 'es'} ({formatHours(contactsMinutes)}h)
          </span>
        )}
      </>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="time-saved-breakdown-modal"
        data-bucket={bucket ?? ''}
        className="max-w-[min(96vw,80rem)] w-[min(96vw,80rem)] max-h-[88vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle data-testid="time-saved-breakdown-title">
            Time saved — {titleLabel}
          </DialogTitle>
          <DialogDescription
            data-testid="time-saved-breakdown-subtitle"
            className="text-xs text-muted-foreground"
          >
            Per-request details in this bucket. Click a column header to sort;
            tap a user to open their full drill-down.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <TraceTable
            rows={sorted}
            columns={COLUMNS}
            sort={sort}
            onSort={onSort}
            from={from}
            to={to}
            isLoading={q.isLoading}
            isError={q.isError}
            onRetry={() => void q.refetch()}
            emptyMessage="No requests in this bucket."
            testIdPrefix="time-saved-breakdown"
            expandable
            expandedIds={expandedIds}
            onToggleExpanded={handleToggleExpanded}
          />
        </div>

        {footer && (
          <div
            data-testid="time-saved-breakdown-footer"
            className="border-t border-border pt-2 text-xs tabular-nums text-muted-foreground"
          >
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
